import { Card, FormHelperText, LinearProgress, Stack, Typography } from '@mui/material';
import { Button } from '@nps.today/component-library';
import AccessGuard, { legacyRoleRules, useAccessControl } from 'access';
import { TimeInput } from 'components/editor/TimeSpanPicker';
import { useRegisterUnsavedFormChanges } from 'components/form/useRegisterUnsavedFormChanges';
import { Form, FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import useCustomDispatch from 'redux/dispatch';
import { getAutomaticAnonymization, setActivityLogAnonymization } from 'redux/slices/organization';
import { RootState, useSelector } from 'redux/store';
import { useTranslator } from 'translation/useTranslator';
import * as Yup from 'yup';

const MIN_DAYS = 30;
const MAX_DAYS = 1825;
const DEFAULT_DAYS = 1095;

const ActivityLogAnonymizationCard = () => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const { canEdit } = useAccessControl(legacyRoleRules.adminManage);
  const { anonymizationConfiguration, loading } = useSelector(
    (state: RootState) => state.organization
  );
  const [isInitialFetched, setIsInitialFetched] = useState(false);

  const isGetLoading = Boolean(loading.getAutomaticAnonymization);
  useEffect(() => {
    if (!isGetLoading && !isInitialFetched) {
      setIsInitialFetched(true);
    }
  }, [isGetLoading, isInitialFetched]);

  const RetentionSchema = Yup.object().shape({
    activityLogAnonymizeAfterDays: Yup.number()
      .required(translator.activityLogRetentionRange())
      .min(MIN_DAYS, translator.activityLogRetentionRange())
      .max(MAX_DAYS, translator.activityLogRetentionRange())
  });

  const formik = useFormik<{ activityLogAnonymizeAfterDays: number }>({
    enableReinitialize: true,
    initialValues: {
      activityLogAnonymizeAfterDays:
        anonymizationConfiguration?.activityLogAnonymizeAfterDays ?? DEFAULT_DAYS
    },
    validationSchema: RetentionSchema,
    onSubmit: async () => {
      customDispatch({
        action: setActivityLogAnonymization,
        actionParameters: {
          activityLogAnonymizeAfterDays: values.activityLogAnonymizeAfterDays,
          anonymizeAfterDays: anonymizationConfiguration?.anonymizeAfterDays ?? null,
          anonymizeEmployees: anonymizationConfiguration?.anonymizeEmployees ?? false,
          anonymizeCompanies: anonymizationConfiguration?.anonymizeCompanies ?? false,
          anonymizeUsers: anonymizationConfiguration?.anonymizeUsers ?? false
        },
        onSuccess: () => {
          customDispatch({
            action: getAutomaticAnonymization,
            disableSuccessMessage: true
          });
        }
      });
    }
  });

  const { errors, values, setFieldValue, handleSubmit, dirty, isSubmitting } = formik;

  useRegisterUnsavedFormChanges(dirty);

  const getDays = (days: number) => days % 365;
  const getYears = (days: number) => Math.floor(days / 365);

  const handleRetentionChange = ({ years, days }: { years: number | null; days: number | null }) => {
    years ??= getYears(values.activityLogAnonymizeAfterDays);
    days ??= getDays(values.activityLogAnonymizeAfterDays);
    setFieldValue('activityLogAnonymizeAfterDays', years * 365 + days);
  };

  if (isGetLoading && !isInitialFetched) {
    return <LinearProgress />;
  }

  if (!isInitialFetched) {
    return null;
  }

  return (
    <AccessGuard rule={legacyRoleRules.adminManage}>
      <FormikProvider value={formik}>
        <Form id="activityLogAnonymization" noValidate onSubmit={handleSubmit}>
          <Card sx={{ p: 3 }} style={{ maxWidth: '30rem' }}>
            <Stack spacing={3} alignItems="flex-end">
              <Stack spacing={2} width="100%">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography>{translator.activityLogAnonymization()}</Typography>
                  <FormHelperText>{translator.activityLogAnonymizationDescription()}</FormHelperText>
                </div>
                <Stack flexDirection="row" gap="1rem" alignItems="center">
                  <Typography>{translator.anonymizeActorNamesAfter()}</Typography>
                  <TimeInput
                    label={translator.years()}
                    value={getYears(values.activityLogAnonymizeAfterDays)}
                    setValue={(newValue: string | number) => {
                      handleRetentionChange({
                        years: typeof newValue === 'string' ? Number(newValue) : newValue,
                        days: null
                      });
                    }}
                    range={5}
                    disabled={!canEdit}
                    hasError={Boolean(errors.activityLogAnonymizeAfterDays)}
                  />
                  <TimeInput
                    label={translator.days()}
                    value={getDays(values.activityLogAnonymizeAfterDays)}
                    setValue={(newValue: string | number) => {
                      handleRetentionChange({
                        years: null,
                        days: typeof newValue === 'string' ? Number(newValue) : newValue
                      });
                    }}
                    range={364}
                    disabled={!canEdit}
                    hasError={Boolean(errors.activityLogAnonymizeAfterDays)}
                  />
                </Stack>
                {errors.activityLogAnonymizeAfterDays && (
                  <FormHelperText error>{errors.activityLogAnonymizeAfterDays}</FormHelperText>
                )}
              </Stack>
              {dirty && (
                <Button
                  dataTestId="activity-log-anonymization-save"
                  onClick={() => handleSubmit()}
                  variant="contained"
                  loading={isSubmitting || Boolean(loading.setActivityLogAnonymization)}
                  disabled={
                    !dirty ||
                    isSubmitting ||
                    isGetLoading ||
                    Boolean(loading.setActivityLogAnonymization) ||
                    !canEdit
                  }
                >
                  {translator.save()}
                </Button>
              )}
            </Stack>
          </Card>
        </Form>
      </FormikProvider>
    </AccessGuard>
  );
};

export default ActivityLogAnonymizationCard;
